import { Form } from 'antd';
import type { NamePath } from '@xbcb/ui-types';
import type { WorkOrderRecordType } from '@xbcb/shared-types';
import {
  ComplianceCheck,
  ComplianceCheckStatus,
  ComplianceCheckType,
} from '__generated__/graphql';
import type { ClassificationState } from 'components/LineItemTariffs/HtsClassification/types';
import { isReasonabilityApplicable } from 'libs/complianceChecks';
import { useComplianceCheckStatus } from 'libs/hooks/useComplianceCheckStatus';
import {
  useReasonabilityDetails,
  ReasonabilityDetails,
} from 'libs/hooks/useReasonabilityDetails';
import { useAutoValidateHts } from 'libs/hooks/useAutoValidateHts';

interface UseReasonabilityLineStateParams {
  linePath: NamePath;
  workOrderType?: WorkOrderRecordType;
  classificationState: ClassificationState;
  /** Whether the "Validate HTS" action is available for this line. */
  showValidateButton: boolean;
  productDetailsReady: boolean;
  /** True while a classification is loading (restore or validation in flight). */
  classificationLoading: boolean;
  userId?: string;
  onValidate: () => void;
}

export interface ReasonabilityLineState {
  showReasonabilityCard: boolean;
  reasonabilityStatus?: ComplianceCheckStatus;
  htsValidationPassed: boolean;
  htsValidationReasoning?: string;
  /**
   * True when the manual-override checkbox should be shown: a FAILED line whose
   * AI recommendation was declined (so it is not yet resolved for submission).
   */
  showManualOverride: boolean;
  details: ReasonabilityDetails;
}

// Derives the line-item reasonability card state and auto-runs HTS validation
// on a FAILED line. Auto-validation only fires for a line with no prior HTS
// classification — a line that already has one is handled by the existing
// mount restore (which re-surfaces its AI_VALIDATED / ACCEPTED / etc. state).
export const useReasonabilityLineState = ({
  linePath,
  workOrderType,
  classificationState,
  showValidateButton,
  productDetailsReady,
  classificationLoading,
  userId,
  onValidate,
}: UseReasonabilityLineStateParams): ReasonabilityLineState => {
  const form = Form.useFormInstance();
  const reasonabilityStatus = useComplianceCheckStatus(
    linePath,
    ComplianceCheckType.REASONABILITY,
  );

  const complianceChecks = form.getFieldValue([
    ...(linePath as (string | number)[]),
    'complianceChecks',
  ]) as ComplianceCheck[] | undefined;
  const hasExistingClassification = Boolean(
    complianceChecks?.some(
      (check) => check.type === ComplianceCheckType.HTS_CLASSIFICATION,
    ),
  );

  const showReasonabilityCard =
    isReasonabilityApplicable(workOrderType) &&
    [
      ComplianceCheckStatus.COMPLETED,
      ComplianceCheckStatus.FAILED,
      ComplianceCheckStatus.IN_PROGRESS,
    ].includes(reasonabilityStatus as ComplianceCheckStatus);

  const details = useReasonabilityDetails({
    linePath,
    status: reasonabilityStatus,
    userId,
  });

  const htsValidationPassed =
    showReasonabilityCard &&
    classificationState.confirmationAlert?.type === 'success';

  const htsValidationReasoning =
    classificationState.confirmationAlert?.reasoningTrace
      ?.slice()
      .sort((a, b) => a.step - b.step)
      .map((step) => step.content)
      .join(' ');

  const autoValidateActive =
    showReasonabilityCard &&
    reasonabilityStatus === ComplianceCheckStatus.FAILED &&
    showValidateButton &&
    productDetailsReady &&
    !classificationLoading &&
    !hasExistingClassification;

  useAutoValidateHts({ active: autoValidateActive, validate: onValidate });

  // The manual-override checkbox lets the operator clear a FAILED verdict after
  // declining the recommendation. A declined recommendation surfaces as the
  // info-type confirmation alert.
  const showManualOverride =
    showReasonabilityCard &&
    reasonabilityStatus === ComplianceCheckStatus.FAILED &&
    classificationState.confirmationAlert?.type === 'info';

  return {
    showReasonabilityCard,
    reasonabilityStatus,
    htsValidationPassed,
    htsValidationReasoning,
    showManualOverride,
    details,
  };
};
